// src/components/Adviser/Pages/LicenseCertificatesBulkUploadPage.jsx

import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../../../DesignSystem/Sidebar";
import PrimaryButton from "../../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../../DesignSystem/Buttons/SecondaryButton";
import Field from "../../../../DesignSystem/Inputs/Field";
import { PostCard } from "../../../Tabs/HelperFunctions/HelperFunctions";
import {
  commitAdviserLicenseCertificatesPreview,
  previewAdviserLicenseCertificatesFromCsvText,
  previewAdviserLicenseCertificatesFromGoogleSheet,
} from "../utils/bulkUploadAdviserLicenseCertificates";
import {
  COMPETENT_ADVISER_TEST_FIELDS,
  LICENSE_CERTIFICATE_SECTIONS,
} from "../utils/LicenseCertificateFields";
import "../../../../../css/Adviser/EditAdviserDetailsPage.css";

const DEFAULT_SHEET_URL =
  "https://docs.google.com/spreadsheets/d/1tjY6SwMfiDpG0PLLqR4Sm_-xOAAvoAhsvweI0qI17-g/edit?gid=0#gid=0";

const EMPTY_VALUE = "—";

const PREVIEW_LABELS = {
  dplName: "DPL Name",
  dplRegistrationNumber: "DPL Reg No.",
  dplExpiryDate: "DPL Expiry",

  spsAccreditedBody: "SPS Body",
  spsExpiryDate: "SPS Expiry",

  fitAndProperExpiryDate: "F&P Expiry",

  moneyLaunderingRegulationsPdp: "MLR PDP",
  gdprAwarenessPdp: "GDPR PDP",
  complianceReadingPdp: "Compliance PDP",
  mortgageAndProtectionPdp: "Mortgage PDP",
  protectionTest: "Protection",
  pensionSwitchAndDefinedContributionsPdp: "Pension Switch",
  rriarTest: "RRIAR",
  vct: "VCT",
  longTermCareTest: "LTC",
  equityReleaseTest: "Equity Release",
  consumerDutyTest: "Consumer Duty",

  appointmentDate: "Appointment",
  preCASSignOff: "Pre-CAS",
  lastOcaMeeting: "Last OCA",
  nextOcaMeeting: "Next OCA",

  notes: "Notes",
};

const getPreviewColumns = () => {
  const columns = [];

  LICENSE_CERTIFICATE_SECTIONS.forEach((section) => {
    if (section.key === "competentAdviser") {
      COMPETENT_ADVISER_TEST_FIELDS.forEach((field) => {
        columns.push({
          sectionKey: section.key,
          sectionTitle: section.title,
          fieldKey: field.key,
          label: field.label,
          shortLabel: PREVIEW_LABELS[field.key] || field.label,
        });
      });

      columns.push({
        sectionKey: section.key,
        sectionTitle: section.title,
        fieldKey: "notes",
        label: "Notes",
        shortLabel: "Notes",
      });

      return;
    }

    section.fields.forEach((field) => {
      columns.push({
        sectionKey: section.key,
        sectionTitle: section.title,
        fieldKey: field.key,
        label: field.label,
        shortLabel: PREVIEW_LABELS[field.key] || field.label,
      });
    });
  });

  return columns;
};

const PREVIEW_COLUMNS = getPreviewColumns();

const PREVIEW_COLUMN_GROUPS = PREVIEW_COLUMNS.reduce((acc, column) => {
  const existing = acc.find((item) => item.sectionKey === column.sectionKey);

  if (existing) {
    existing.columns.push(column);
    return acc;
  }

  acc.push({
    sectionKey: column.sectionKey,
    sectionTitle: column.sectionTitle,
    columns: [column],
  });

  return acc;
}, []);

const getPreviewValue = (row, column) => {
  const value = row?.payload?.[column.sectionKey]?.[column.fieldKey];

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : EMPTY_VALUE;
  }

  if (value === null || value === undefined) {
    return EMPTY_VALUE;
  }

  const text = String(value).trim();

  return text || EMPTY_VALUE;
};

const ResultBlock = ({ result }) => {
  if (!result) return null;

  return (
    <PostCard title="Upload Result">
      <div className="smGrid2">
        <div className="Content-Small">Total rows: {result.totalRows}</div>
        <div className="Content-Small">Parsed rows: {result.parsedRows}</div>
        <div className="Content-Small">Matched advisers: {result.matched}</div>
        <div className="Content-Small">Written: {result.written}</div>
        <div className="Content-Small">
          Skipped: {result.skipped?.length || 0}
        </div>
        <div className="Content-Small">
          Not found: {result.notFound?.length || 0}
        </div>
      </div>
    </PostCard>
  );
};

const PreviewBlock = ({ preview }) => {
  const rows = useMemo(() => {
    return Array.isArray(preview?.previewRows) ? preview.previewRows : [];
  }, [preview]);

  if (!preview) return null;

  return (
    <PostCard title="Preview Before Upload">
      <div className="smGrid2">
        <div className="Content-Small">Total rows: {preview.totalRows}</div>
        <div className="Content-Small">Parsed rows: {preview.parsedRows}</div>
        <div className="Content-Small">Matched advisers: {preview.matched}</div>
        <div className="Content-Small">
          Skipped rows: {preview.skipped?.length || 0}
        </div>
        <div className="Content-Small">
          Not found: {preview.notFound?.length || 0}
        </div>
        <div className="Content-Small">
          Will write: {preview.matchedRows?.length || 0}
        </div>
      </div>

      {preview.notFound?.length > 0 ? (
        <div className="adviserLicenseWarning Content-Small">
          Some adviser emails were not found. Those rows will not be uploaded.
        </div>
      ) : null}

      {preview.skipped?.length > 0 ? (
        <div className="smImportList">
          <div className="FP_Subtitle_Semibold">Skipped rows</div>

          {preview.skipped.slice(0, 50).map((item) => (
            <div
              key={`skipped-${item.rowNumber}-${item.reason}`}
              className="Content-Small"
            >
              Row {item.rowNumber}: {item.reason}
            </div>
          ))}
        </div>
      ) : null}

      <div className="smImportTableWrap">
        <table className="smImportPreviewTable smImportPreviewTableWide">
          <thead>
            <tr>
              <th className="smStickyCol smStickyCol1" rowSpan={2}>
                Row
              </th>
              <th className="smStickyCol smStickyCol2" rowSpan={2}>
                Status
              </th>
              <th className="smStickyCol smStickyCol3" rowSpan={2}>
                Adviser Email
              </th>

              {PREVIEW_COLUMN_GROUPS.map((group) => (
                <th
                  key={`preview-group-${group.sectionKey}`}
                  className="smImportGroupHeader"
                  colSpan={group.columns.length}
                >
                  {group.sectionTitle}
                </th>
              ))}
            </tr>

            <tr>
              {PREVIEW_COLUMNS.map((column) => (
                <th
                  key={`preview-head-${column.sectionKey}-${column.fieldKey}`}
                  title={column.label}
                  className="smImportColumnHeader"
                >
                  {column.shortLabel}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.slice(0, 200).map((row) => (
              <tr
                key={`preview-${row.rowNumber}-${row.adviserEmail}`}
                className={
                  row.status === "Matched"
                    ? "smImportRowMatched"
                    : "smImportRowNotFound"
                }
              >
                <td className="smStickyCol smStickyCol1">{row.rowNumber}</td>

                <td className="smStickyCol smStickyCol2">
                  <span
                    className={
                      row.status === "Matched"
                        ? "smImportStatusPill smImportStatusMatched"
                        : "smImportStatusPill smImportStatusNotFound"
                    }
                  >
                    {row.status}
                  </span>
                </td>

                <td className="smStickyCol smStickyCol3">
                  {row.adviserEmail || EMPTY_VALUE}
                </td>

                {PREVIEW_COLUMNS.map((column) => {
                  const value = getPreviewValue(row, column);
                  const isEmpty = value === EMPTY_VALUE;

                  return (
                    <td
                      key={`preview-cell-${row.rowNumber}-${column.sectionKey}-${column.fieldKey}`}
                      title={isEmpty ? "" : value}
                      className={isEmpty ? "smImportEmptyCell" : ""}
                    >
                      {value}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length > 200 ? (
        <div className="Content-Small muted">
          Showing first 200 preview rows out of {rows.length}.
        </div>
      ) : null}
    </PostCard>
  );
};

export default function LicenseCertificatesBulkUploadPage() {
  const [sheetUrl, setSheetUrl] = useState(DEFAULT_SHEET_URL);
  const [csvFile, setCsvFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importError, setImportError] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const hasMatchedRows = preview?.matchedRows?.length > 0;

  const handleGoogleSheetPreview = async () => {
    try {
      setLoading(true);
      setImportError("");
      setPreview(null);
      setResult(null);

      const previewResult =
        await previewAdviserLicenseCertificatesFromGoogleSheet(sheetUrl);

      setPreview(previewResult);
    } catch (error) {
      console.error("Google Sheet preview failed:", error);
      setImportError(error.message || "Google Sheet preview failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleCsvPreview = async () => {
    try {
      setLoading(true);
      setImportError("");
      setPreview(null);
      setResult(null);

      if (!csvFile) {
        throw new Error("Please choose a CSV file first.");
      }

      const csvText = await csvFile.text();

      const previewResult = await previewAdviserLicenseCertificatesFromCsvText(
        csvText,
        {
          sourceUrl: csvFile.name,
          sourceType: "csv-file",
        },
      );

      setPreview(previewResult);
    } catch (error) {
      console.error("CSV preview failed:", error);
      setImportError(error.message || "CSV preview failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmUpload = async () => {
    try {
      setLoading(true);
      setImportError("");
      setResult(null);

      if (!preview) {
        throw new Error("Please preview the import before uploading.");
      }

      const uploadResult =
        await commitAdviserLicenseCertificatesPreview(preview);

      setResult(uploadResult);
    } catch (error) {
      console.error("Licence certificate upload failed:", error);
      setImportError(error.message || "Upload failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleClearPreview = () => {
    setPreview(null);
    setResult(null);
    setImportError("");
  };

  return (
    <div className="main-content">
      <Helmet>
        <title>Bulk Upload Licence Certificates</title>
      </Helmet>

      <Sidebar />

      <div className="Main-Container">
        <div className="smFeed">
          <div className="smEditActions">
            <div>
              <h3 className="FP_H3">Bulk Upload Licence Certificates</h3>
              <p className="Content-Small muted">
                Preview the upload first, then confirm before writing anything
                to Firestore.
              </p>
            </div>
          </div>

          {importError ? (
            <div className="adviserLicenseError Content-Small">
              {importError}
            </div>
          ) : null}

          <PostCard title="Preview from Google Sheet">
            <div className="smGrid2">
              <Field
                label="Google Sheet URL"
                full
                value={sheetUrl}
                onChange={setSheetUrl}
                placeholder="Paste Google Sheet URL"
              />
            </div>

            <div className="smEditActionsRight">
              <PrimaryButton
                label={loading ? "Checking..." : "Preview Google Sheet"}
                onClick={handleGoogleSheetPreview}
                disabled={loading || !sheetUrl}
              />
            </div>
          </PostCard>

          <PostCard title="Preview from CSV">
            <div className="smImportFileBox">
              <label className="smFieldLabel">CSV file</label>

              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(event) => {
                  setCsvFile(event.target.files?.[0] || null);
                  setPreview(null);
                  setResult(null);
                }}
              />

              {csvFile ? (
                <div className="Content-Small muted">
                  Selected: {csvFile.name}
                </div>
              ) : null}
            </div>

            <div className="smEditActionsRight">
              <SecondaryButton
                label={loading ? "Checking..." : "Preview CSV"}
                onClick={handleCsvPreview}
                disabled={loading || !csvFile}
              />
            </div>
          </PostCard>

          <PreviewBlock preview={preview} />

          {preview ? (
            <PostCard title="Confirm Upload">
              <p className="Content-Small muted">
                This will upload only the matched adviser rows to Firestore. Not
                found and skipped rows will be ignored.
              </p>

              <div className="smEditActionsRight">
                <SecondaryButton
                  label="Clear Preview"
                  onClick={handleClearPreview}
                  disabled={loading}
                />

                <PrimaryButton
                  label={loading ? "Uploading..." : "Confirm Upload to DB"}
                  onClick={handleConfirmUpload}
                  disabled={loading || !hasMatchedRows}
                />
              </div>
            </PostCard>
          ) : null}

          <ResultBlock result={result} />
        </div>
      </div>
    </div>
  );
}
